import { createElement, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MdNotifications } from 'react-icons/md';
import { API_BASE_URL } from '@api/config';
import { refreshAuthToken } from '@api/client';
import { getStoredUser } from '@utils/userSession';
import useAuth from '@contexts/useAuth';
import useSnackbar from '@hooks/useSnackbar';
import { useSseStream, type SseEvent } from '@hooks/useSseStream';
import type { NotificationDto } from '@api/notifications';
import { notificationKeys } from './notificationQueryKeys';

const STREAM_PATH = '/api/v1/notifications/stream';
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
const RECONNECT_JITTER_MS = 500;

const computeBackoff = (attempt: number): number => {
  const exp = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
  const jitterFraction = crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32;
  return exp + Math.floor(jitterFraction * RECONNECT_JITTER_MS);
};

/**
 * Abort-aware sleep — resolves when the timer fires OR when the signal
 * aborts (whichever is first). Without this, teardown blocks behind the
 * full backoff delay (up to 30s) every time the hook unmounts mid-wait.
 */
const sleep = (ms: number, signal: AbortSignal): Promise<void> => {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', onAbort);
      resolve();
    };
    signal.addEventListener('abort', onAbort);
  });
};

const openStream = async (token: string, signal: AbortSignal): Promise<Response> => {
  return fetch(`${API_BASE_URL}${STREAM_PATH}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'text/event-stream',
      'Cache-Control': 'no-cache',
    },
    signal,
  });
};

const fetchStreamWithRefresh = async (
  token: string,
  signal: AbortSignal,
): Promise<Response> => {
  const response = await openStream(token, signal);
  if (response.status === 401 || response.status === 403) {
    const refreshed = await refreshAuthToken();
    if (refreshed) return openStream(refreshed, signal);
  }
  return response;
};

/**
 * Long-lived Server-Sent Events client for real-time notification fan-out.
 *
 * <p>Behaviour:</p>
 * <ul>
 *   <li>Opens an authenticated SSE connection to {@code /api/v1/notifications/stream}.</li>
 *   <li>On {@code notification} events, invalidates the relevant React Query caches
 *       (list + unread count) and shows a transient toast.</li>
 *   <li>On disconnect (timeout / network error), reconnects with exponential
 *       backoff and jitter, capped at {@link #RECONNECT_MAX_MS}.</li>
 *   <li>Closes the stream on unmount or when the user logs out.</li>
 * </ul>
 *
 * <p>The unread badge in the profile menu has its own polling fallback so
 * the UI degrades gracefully if SSE is permanently unavailable (corporate
 * proxies, etc.).</p>
 */
export function useNotificationStream(): void {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { user } = useAuth();

  const userIdRef = useRef<string | null>(user?.id ?? null);
  userIdRef.current = user?.id ?? null;

  const showSnackbarRef = useRef(showSnackbar);
  showSnackbarRef.current = showSnackbar;

  const handleSseEvent = useCallback(
    (event: SseEvent) => {
      if (event.type === 'connected' || event.type === 'heartbeat') return;
      if (event.type !== 'notification') return;

      const dto = event.data as unknown as NotificationDto;
      if (!dto?.id) return;

      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });

      showSnackbarRef.current({
        text: dto.title,
        variant: 'snackbar',
        leadingIcon: createElement(MdNotifications, { size: 20, 'aria-hidden': true }),
      });
    },
    [queryClient],
  );

  const { readStream, abort } = useSseStream(handleSseEvent);

  useEffect(() => {
    const userId = userIdRef.current;
    if (!userId) return;

    const abortController = new AbortController();
    const state = { cancelled: false, attempt: 0 };

    const runOneAttempt = async (): Promise<void> => {
      const token = getStoredUser()?.token;
      if (!token) {
        await sleep(RECONNECT_MAX_MS, abortController.signal);
        return;
      }
      try {
        const response = await fetchStreamWithRefresh(token, abortController.signal);
        if (!response.ok || !response.body) {
          throw new Error(`Notification stream open failed: ${response.status}`);
        }
        state.attempt = 0;
        await readStream(response.body.getReader());
      } catch {
        // swallow — the loop decides whether to back off or abort.
      }
    };

    const isAborted = (): boolean => state.cancelled || abortController.signal.aborted;

    const connect = async (): Promise<void> => {
      while (!state.cancelled) {
        await runOneAttempt();
        if (isAborted()) return;

        const delay = computeBackoff(state.attempt);
        state.attempt += 1;
        await sleep(delay, abortController.signal);
      }
    };

    connect().catch(() => undefined);

    return () => {
      state.cancelled = true;
      // Order matters: WebKit raises an unhandled AbortError if the controller is
      // aborted after the body stream has already been released by abort().
      abortController.abort();
      abort();
    };
  }, [user?.id, readStream, abort]);
}
